import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getWarranties,
  upsertWarranty,
  deleteWarranty,
} from "../../api/warranties";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import LoadingSpinner from "../../components/LoadingSpinner";

const TERMS = [3, 6, 12, 18, 24, 48];

export default function WarrantyAdmin() {
  const qc = useQueryClient();
  const { data: warranties = [], isLoading } = useQuery({
    queryKey: ["warranties"],
    queryFn: getWarranties,
  });

  const [packages, setPackages] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [packageToDeleteIdx, setPackageToDeleteIdx] = useState(null);

  useEffect(() => {
    if (warranties.length > 0) {
      const initialized = warranties.map((w) => ({
        ...w,
        expanded: false,
        editMode: false,
      }));
      setPackages(initialized);
    } else {
      setPackages([]); // clear completely if nothing in DB
    }
  }, [warranties]);

  const { mutate: savePackage, isLoading: isSaving } = useMutation({
    mutationFn: upsertWarranty,
    onSuccess: () => {
      qc.invalidateQueries(["warranties"]);
      toast.success("✔️ Package saved");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Save failed");
    },
  });

  const { mutate: removePackage, isLoading: isDeleting } = useMutation({
    mutationFn: deleteWarranty,
    onSuccess: () => {
      qc.invalidateQueries(["warranties"]);
      toast.success("🗑️ Package deleted");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Delete failed");
    },
  });

  const addPackage = () => {
    setPackages((prev) => [
      {
        packageName: "",
        terms: [{ term: "", options: [{ price: "", perRepairLimit: "" }] }],
        editMode: true,
        expanded: true,
      },
      ...prev,
    ]);
  };

  const toggleExpand = (idx) =>
    setPackages((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, expanded: !p.expanded } : p))
    );

  const toggleEdit = (idx, state) => {
    setPackages((prev) => {
      // If exiting edit mode AND this is a new package without _id, remove it
      if (!state && !prev[idx]._id) {
        const filtered = prev.filter((_, i) => i !== idx);
        // Collapse all remaining packages to avoid unintended open state
        return filtered.map((p) => ({
          ...p,
          expanded: false,
          editMode: false,
        }));
      }

      return prev.map((p, i) =>
        i === idx ? { ...p, editMode: state, expanded: true } : p
      );
    });
  };

  const updatePackageField = (idx, field, value) =>
    setPackages((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p))
    );

  const updateTermField = (pkgIdx, termIdx, key, value) =>
    setPackages((prev) =>
      prev.map((p, i) =>
        i === pkgIdx
          ? {
              ...p,
              terms: p.terms.map((t, j) =>
                j === termIdx ? { ...t, [key]: value } : t
              ),
            }
          : p
      )
    );

  const updateOption = (pkgIdx, termIdx, optIdx, key, value) =>
    setPackages((prev) =>
      prev.map((p, i) =>
        i === pkgIdx
          ? {
              ...p,
              terms: p.terms.map((t, j) =>
                j === termIdx
                  ? {
                      ...t,
                      options: t.options.map((opt, k) =>
                        k === optIdx ? { ...opt, [key]: value } : opt
                      ),
                    }
                  : t
              ),
            }
          : p
      )
    );

  const addTerm = (pkgIdx) =>
    setPackages((prev) =>
      prev.map((p, i) =>
        i === pkgIdx
          ? {
              ...p,
              terms: [
                ...p.terms,
                { term: "", options: [{ price: "", perRepairLimit: "" }] },
              ],
            }
          : p
      )
    );

  const addOption = (pkgIdx, termIdx) =>
    setPackages((prev) =>
      prev.map((p, i) =>
        i === pkgIdx
          ? {
              ...p,
              terms: p.terms.map((t, j) =>
                j === termIdx
                  ? {
                      ...t,
                      options: [
                        ...(t.options || []),
                        { price: "", perRepairLimit: "" },
                      ],
                    }
                  : t
              ),
            }
          : p
      )
    );

  const removeOption = (pkgIdx, termIdx, optIdx) =>
    setPackages((prev) =>
      prev.map((p, i) =>
        i === pkgIdx
          ? {
              ...p,
              terms: p.terms.map((t, j) =>
                j === termIdx
                  ? {
                      ...t,
                      options: t.options.filter((_, k) => k !== optIdx),
                    }
                  : t
              ),
            }
          : p
      )
    );

  const removeTerm = (pkgIdx, termIdx) =>
    setPackages((prev) =>
      prev.map((p, i) =>
        i === pkgIdx
          ? { ...p, terms: p.terms.filter((_, j) => j !== termIdx) }
          : p
      )
    );

  const removePackageRow = (pkgIdx) => {
    const pkg = packages[pkgIdx];
    if (pkg._id) removePackage(pkg._id);

    setPackages(
      (prev) =>
        prev
          .filter((_, i) => i !== pkgIdx)
          .map((p) => ({ ...p, expanded: false, editMode: false })) // collapse others
    );
  };

  const handleSave = (pkgIdx) => {
    const pkg = packages[pkgIdx];

    if (!pkg.packageName.trim()) {
      toast.error("Package name is required");
      return;
    }

    // 🔴 Ensure at least one term exists
    if (!pkg.terms || pkg.terms.length === 0) {
      toast.error("At least one term is required");
      return;
    }

    const nameExists = warranties.some(
      (w) =>
        w.packageName.trim().toLowerCase() ===
          pkg.packageName.trim().toLowerCase() &&
        (!pkg._id || w._id !== pkg._id)
    );
    if (nameExists) {
      toast.error("Package name already exists");
      return;
    }

    const termValues = pkg.terms.map((t) => t.term);
    const hasDuplicateTerms = new Set(termValues).size !== termValues.length;
    if (hasDuplicateTerms) {
      toast.error("Duplicate term values are not allowed");
      return;
    }

    for (const term of pkg.terms) {
      // 🔴 Ensure term is selected
      if (!term.term) {
        toast.error("Each term must have a valid number of months selected");
        return;
      }

      // 🔴 Ensure at least one option exists
      if (!term.options || term.options.length === 0) {
        toast.error(`Term "${term.term}" must have at least one price`);
        return;
      }

      const priceSet = new Set();
      for (const opt of term.options) {
        // 🔴 Validate presence and type of price and cover
        if (
          typeof opt.price !== "number" ||
          typeof opt.perRepairLimit !== "number" ||
          isNaN(opt.price) ||
          isNaN(opt.perRepairLimit)
        ) {
          toast.error(
            `Each option in term "${term.term}" must have valid numeric price and perRepairLimit`
          );
          return;
        }

        if (priceSet.has(opt.price)) {
          toast.error(
            `Duplicate price £${opt.price} in term ${term.term} months`
          );
          return;
        }
        priceSet.add(opt.price);
      }
    }

    savePackage(pkg);
    toggleEdit(pkgIdx, false);
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Warranty Packages</h2>
        <button
          onClick={addPackage}
          className="inline-flex items-center px-3 py-1 bg-surface rounded hover:bg-accent text-sm"
        >
          <PlusIcon className="h-4 w-4 mr-1" /> Add Package
        </button>
      </div>

      {packages.map((pkg, pkgIdx) => (
        <div key={pkg._id || pkgIdx} className="bg-surface rounded shadow mb-4">
          <div
            className="cursor-pointer p-3 border-b flex justify-between items-center"
            onClick={() => toggleExpand(pkgIdx)}
          >
            {pkg.editMode ? (
              <input
                type="text"
                value={pkg.packageName}
                onChange={(e) =>
                  updatePackageField(pkgIdx, "packageName", e.target.value)
                }
                placeholder="Package Name"
                className="border-b px-2 py-1 rounded text-sm font-semibold w-64"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="font-bold">
                {pkg.packageName || "(Unnamed Package)"}
              </span>
            )}

            <div className="space-x-2">
              {pkg.editMode ? (
                <>
                  <button
                    onClick={() => handleSave(pkgIdx)}
                    className="text-green-600"
                  >
                    <CheckIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => toggleEdit(pkgIdx, false)}
                    className="text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // prevent toggleExpand
                      toggleEdit(pkgIdx, true);
                    }}
                    className="text-accent"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // prevent accordion open
                      setPackageToDeleteIdx(pkgIdx);
                      setShowConfirm(true);
                    }}
                    className="text-red-500"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          </div>

          {pkg.expanded && (
            <div className="p-4 space-y-4">
              {pkg.editMode ? (
                <>
                  {/* 🔧 Edit Mode UI (Form Inputs) */}
                  <table className="w-full table-auto text-sm">
                    <thead>
                      <tr>
                        <th className="text-left p-1">Term (Months)</th>
                        <th className="text-left p-1">Price</th>
                        <th className="text-left p-1">Per Repair Limit</th>
                        <th className="text-center p-1">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pkg.terms.map((t, termIdx) => (
                        <React.Fragment key={termIdx}>
                          <tr className="bg-lightsec">
                            <td colSpan={4} className="p-2 font-semibold">
                              Term:{" "}
                              <select
                                value={t.term}
                                onChange={(e) =>
                                  updateTermField(
                                    pkgIdx,
                                    termIdx,
                                    "term",
                                    +e.target.value
                                  )
                                }
                                className="text-primary ml-2 border py-1 rounded text-sm"
                              >
                                <option value="">Select</option>
                                {TERMS.map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => removeTerm(pkgIdx, termIdx)}
                                className="ml-4 text-red-500 text-xs hover:underline"
                              >
                                Remove Term
                              </button>
                            </td>
                          </tr>
                          {t.options?.map((opt, optIdx) => (
                            <tr key={optIdx}>
                              <td></td>
                              <td>
                                <input
                                  type="number"
                                  value={opt.price}
                                  onChange={(e) =>
                                    updateOption(
                                      pkgIdx,
                                      termIdx,
                                      optIdx,
                                      "price",
                                      +e.target.value
                                    )
                                  }
                                  onWheel={(e) => e.target.blur()}
                                  placeholder="Price"
                                  className="border px-2 py-1 rounded w-full"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={opt.perRepairLimit}
                                  onChange={(e) =>
                                    updateOption(
                                      pkgIdx,
                                      termIdx,
                                      optIdx,
                                      "perRepairLimit",
                                      +e.target.value
                                    )
                                  }
                                  onWheel={(e) => e.target.blur()}
                                  placeholder="Per Repair Limit"
                                  className="border px-2 py-1 rounded w-full"
                                />
                              </td>
                              <td className="text-center">
                                <button
                                  onClick={() =>
                                    removeOption(pkgIdx, termIdx, optIdx)
                                  }
                                  className="p-1 text-red-500 hover:bg-red-100 rounded"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={4} className="pl-48 pb-2">
                              <button
                                onClick={() => addOption(pkgIdx, termIdx)}
                                className="text-xs text-accent hover:underline mt-2"
                              >
                                + Add Price
                              </button>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <button
                    onClick={() => addTerm(pkgIdx)}
                    className="mt-2 text-xs text-accent hover:underline"
                  >
                    + Add Term
                  </button>
                </>
              ) : (
                <>
                  {/* 👀 View Mode UI (Read-only Summary) */}
                  {pkg.terms.map((term, termIdx) => (
                    <div key={termIdx} className="w-[50%] mb-4 mx-auto">
                      <h4 className="font-semibold text-accent mb-2">
                        Term: {term.term} months
                      </h4>
                      <table className="w-full text-sm border rounded overflow-hidden">
                        <thead className="bg-gray-100 text-primary">
                          <tr>
                            <th className="py-2 px-3">Price</th>
                            <th className="py-2 px-3">Per Repair Cover</th>
                          </tr>
                        </thead>
                        <tbody>
                          {term.options.map((opt, optIdx) => (
                            <tr key={optIdx} className="border-t text-center">
                              <td className="py-1 px-3">£{opt.price}</td>
                              <td className="py-1 px-3">
                                £{opt.perRepairLimit}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {showConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20">
              <div className="bg-white p-6 rounded shadow-md w-80">
                <h3 className="text-lg font-semibold mb-4 text-center text-accent">
                  Confirm Deletion
                </h3>
                <p className="text-sm text-gray-700 mb-6 text-center">
                  Are you sure you want to delete this package?
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowConfirm(false);
                      setPackageToDeleteIdx(null);
                    }}
                    className="px-3 py-1 text-sm rounded border text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      removePackageRow(packageToDeleteIdx);
                      setShowConfirm(false);
                      setPackageToDeleteIdx(null);
                    }}
                    className="px-3 py-1 text-sm rounded bg-red-500 text-white hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
